import { useContext, useEffect, useRef, MutableRefObject } from "react";
import { CadviewerContext } from "../context/Cadviewer.Context";
import { View } from "../types";

/**
 * Hook that reactively applies the appropriate view whenever a drawing finishes loading.
 * Acts as a FALLBACK to the synchronous EventSystem subscriber in Header.component.tsx.
 *
 * The primary deep link view application now happens synchronously inside cvjs_OnLoadEnd
 * via an EventSystem subscriber (see Header.component.tsx). This hook serves as:
 * 1. Fallback when views/planType aren't ready at the time of cvjs_OnLoadEnd
 * 2. UI sync mechanism for plan changes and dropdown state
 * 3. Deferred re-application safety net
 *
 * The syncViewAppliedRef (from Header) tells this hook whether the sync subscriber
 * already applied the view this cycle, so it can skip redundant work.
 */
export function useDeepLinkView(
  handleSelectedLayer: (layer: View) => void,
  isHome: boolean,
  targetViewName: string | null | undefined,
  currentSelectedViewName: string | null | undefined,
  syncViewAppliedRef?: MutableRefObject<boolean>
) {
  const {
    views,
    currentPlanType,
    drawingLoaded,
    drawingLoadGeneration,
    drawingLoadEndCount,
  } = useContext(CadviewerContext);

  const initialLoadDoneRef = useRef(false);
  const viewAppliedForCurrentLoadRef = useRef(false);

  // Refs for deferred re-application
  const deferredRafRef = useRef<number>(0);
  const deferredTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSelectedLayerRef = useRef(handleSelectedLayer);

  // Keep the handler ref up-to-date so deferred calls always use the latest version
  useEffect(() => {
    handleSelectedLayerRef.current = handleSelectedLayer;
  }, [handleSelectedLayer]);

  /** Cancel any pending deferred re-application */
  const cancelDeferred = () => {
    if (deferredRafRef.current) {
      cancelAnimationFrame(deferredRafRef.current);
      deferredRafRef.current = 0;
    }
    if (deferredTimeoutRef.current) {
      clearTimeout(deferredTimeoutRef.current);
      deferredTimeoutRef.current = null;
    }
  };

  // Cleanup deferred timers on unmount
  useEffect(() => {
    return cancelDeferred;
  }, []);

  // Reset the per-cycle guard when a new drawing load cycle starts.
  useEffect(() => {
    console.log(`[DeepLink] Guard reset via drawingLoadGeneration=${drawingLoadGeneration}`);
    viewAppliedForCurrentLoadRef.current = false;
    // Also reset the sync subscriber flag so it can apply again for this cycle
    if (syncViewAppliedRef) syncViewAppliedRef.current = false;
    cancelDeferred();
  }, [drawingLoadGeneration]);

  // Reset the guard on every cvjs_OnLoadEnd event.
  useEffect(() => {
    console.log(`[DeepLink] Guard reset via drawingLoadEndCount=${drawingLoadEndCount}`);
    viewAppliedForCurrentLoadRef.current = false;
    if (syncViewAppliedRef) syncViewAppliedRef.current = false;
    cancelDeferred();
  }, [drawingLoadEndCount]);

  // Also reset if drawingLoaded explicitly goes false
  useEffect(() => {
    if (!drawingLoaded) {
      console.log("[DeepLink] Guard reset via drawingLoaded=false");
      viewAppliedForCurrentLoadRef.current = false;
      if (syncViewAppliedRef) syncViewAppliedRef.current = false;
      cancelDeferred();
    }
  }, [drawingLoaded]);

  // Main effect: fallback view application when sync subscriber didn't handle it.
  // Also handles deferred re-application as safety net.
  useEffect(() => {
    console.log(`[DeepLink][Effect] triggered — drawingLoaded=${drawingLoaded}, planType=${currentPlanType}, views=${views.length}, isHome=${isHome}, gen=${drawingLoadGeneration}, loadEnd=${drawingLoadEndCount}, guard=${viewAppliedForCurrentLoadRef.current}, syncApplied=${syncViewAppliedRef?.current}`);

    if (!isHome || !drawingLoaded || !currentPlanType || views.length === 0) {
      return;
    }
    if (viewAppliedForCurrentLoadRef.current) {
      console.log("[DeepLink][Effect] SKIP: already applied this cycle");
      return;
    }

    // If the sync subscriber already applied, mark our guard and schedule deferred only
    if (syncViewAppliedRef?.current) {
      console.log("[DeepLink][Effect] Sync subscriber already applied — scheduling deferred re-application only");
      viewAppliedForCurrentLoadRef.current = true;
      initialLoadDoneRef.current = true;

      // Find the view that was applied to schedule deferred re-application
      const viewList = views.find((vp) => vp.planType === currentPlanType)?.views ?? [];
      const tvn = targetViewName;
      const csvn = currentSelectedViewName;
      let appliedView: View | undefined;
      if (!initialLoadDoneRef.current && tvn) {
        appliedView = viewList.find((v) => (v.view || v.View) === tvn);
      }
      if (!appliedView && csvn) {
        appliedView = viewList.find((v) => (v.view || v.View) === csvn);
      }
      if (!appliedView && tvn) {
        appliedView = viewList.find((v) => (v.view || v.View) === tvn);
      }
      if (!appliedView) {
        appliedView = viewList.find((v) => v.selected) ?? viewList[0];
      }

      if (appliedView) {
        cancelDeferred();
        const capturedView = appliedView;
        deferredRafRef.current = requestAnimationFrame(() => {
          deferredTimeoutRef.current = setTimeout(() => {
            console.log(`[DeepLink][Effect] Deferred re-application: "${capturedView.view || capturedView.View}"`);
            handleSelectedLayerRef.current(capturedView);
          }, 150);
        });
      }
      return;
    }

    // Sync subscriber did NOT apply — do full fallback application
    const viewList =
      views.find((vp) => vp.planType === currentPlanType)?.views ?? [];

    if (viewList.length === 0) {
      return;
    }

    let viewToApply: View | undefined;

    if (!initialLoadDoneRef.current && targetViewName) {
      viewToApply = viewList.find(
        (v) => (v.view || v.View) === targetViewName
      );
      console.log(`[DeepLink][Effect] First load: target "${targetViewName}" → ${viewToApply ? "found" : "not found"}`);
    }

    if (!viewToApply && initialLoadDoneRef.current && currentSelectedViewName) {
      viewToApply = viewList.find(
        (v) => (v.view || v.View) === currentSelectedViewName
      );
      console.log(`[DeepLink][Effect] Plan change: current "${currentSelectedViewName}" → ${viewToApply ? "found" : "not found"}`);
    }

    if (!viewToApply && targetViewName) {
      viewToApply = viewList.find(
        (v) => (v.view || v.View) === targetViewName
      );
    }

    if (!viewToApply) {
      viewToApply = viewList.find((v) => v.selected) ?? viewList[0];
      console.log(`[DeepLink][Effect] Fallback default → "${viewToApply?.view || viewToApply?.View}"`);
    }

    if (viewToApply) {
      console.log(`[DeepLink][Effect] Applying view (fallback): "${viewToApply.view || viewToApply.View}"`);
      handleSelectedLayer(viewToApply);
      viewAppliedForCurrentLoadRef.current = true;
      initialLoadDoneRef.current = true;

      cancelDeferred();
      const capturedView = viewToApply;
      deferredRafRef.current = requestAnimationFrame(() => {
        deferredTimeoutRef.current = setTimeout(() => {
          console.log(`[DeepLink][Effect] Deferred re-application: "${capturedView.view || capturedView.View}"`);
          handleSelectedLayerRef.current(capturedView);
        }, 150);
      });
    }
  }, [views, currentPlanType, drawingLoaded, drawingLoadGeneration, drawingLoadEndCount, isHome, handleSelectedLayer, targetViewName, currentSelectedViewName]);
}
